// @givmo/app-charity — this host's words for the shared org console (CBO-CT1 slice 6).
//
// ══ THIS FILE IS THE ENTITY-CLASS SEAM ═════════════════════════════════════════════════════════
//
// `@givmo/org-console` is built ONCE, keyed on `org_ref`, and it never learns what kind of
// organization it is rendering — the backend resolves the class from `(user, org_ref)` precisely so
// the wire never carries one, because a client that could name its own class is a client that could
// pick its own authorization rules. The class enters the web half here, as DATA: strings and label
// functions the package renders verbatim and never branches on.
//
// So a second host is a MOUNT, not a fork. It supplies its own noun and its own module vocabulary and
// gets the same machinery. Nothing about this file is special to it beyond the words.
//
// ══ THE SLUGS AND KEYS BELOW ARE THE BACKEND'S, NOT THIS APP'S ═════════════════════════════════
//
// Every entry is copied from `givmo-backend-python` `app/domain/charity_module.py` — the charity
// instance of the org-console catalog contract. That file is the authority for BOTH halves: the
// module slugs are what `CharityModule` declares, and the permission keys are exactly the strings
// its three `ProductCatalog` entries carry.
//
// ⚠ A LABEL FOR A KEY THAT IS NOT IN A CATALOG IS DEAD COPY, and it is worse than none: it reads as
// coverage while every real control beside it renders a raw identifier. So this table is written
// against the catalog rather than against what the portal happens to have screens for. Re-derive it
// at the ref when the catalog moves:
//   git -C ~/Developer/givmo/givmo-backend-python show origin/development:app/domain/charity_module.py
//
// ⚠ AN UNKNOWN SLUG OR KEY STILL RENDERS. The package titleizes a slug it has no word for and prints
// a permission key as itself, so a module the backend launches before this app ships copy for it
// reads as a module rather than as an empty cell beside a live control. That floor is deliberate —
// it is not a reason to leave this table stale, because a raw `board_conflict_disclosure:manage`
// beside a checkbox is actionable and obviously a gap, while a person still has to decide.

import { titleizeSlug, type OrgConsoleVocab, type OrgDeactivationCopy } from '@givmo/org-console';
import { ADMIN_CONSOLE_LABEL, ADMIN_CONSOLE_SLUG } from '../../console/module-table';

/**
 * What org-account deactivation does to a CHARITY, as data (Org Machinery OM-9; the copy RULED
 * 2026-09-05 — no support address on this class's dialog: re-claim is self-serve).
 *
 * Every line is a statement of what the act does, in reading order; the re-enrollment truth for the
 * class is last — a deactivated charity is re-claimed, never restored. The act touches neither the
 * catalog (removal is its own request, on `/removal`) nor money. The collective noun is
 * "everyone" / "people": Global Admin, Owner and Member are the three role words and a fourth is
 * banned (the vocabulary rule). The deleted line names its one exception wherever the charity
 * entered a physical address, shown on the page or not, which the act clears (D-CPO-62; D-CPO-69
 * question 8's condition as OM-92 widened it, on the dialog as on the card).
 */
export const CHARITY_DEACTIVATION_COPY: OrgDeactivationCopy = {
  noun: 'charity',
  consequences: [
    'Everyone loses access now — Global Admins, Owners and Members, including you.',
    'Pending invites and access requests are cancelled.',
    'The charity stays listed on Givmo — removing it from the catalog is a separate request.',
    'Payouts already owed to the charity still go out.',
    {
      line: 'Nothing is deleted.',
      whereEnteredAddress: 'Nothing is deleted except the physical address you entered.',
    },
    'You can claim the charity again later.',
  ],
};

/**
 * The class's display names for the shared module authority's catalog entries.
 *
 * `board` flipped to grant-by-exception on 2026-09-04 and `compliance` flips with CCM-MOD1B (backend
 * `feat/ccm-mod1b-compliance-standing`, D-CPO-24): a non-Global-Admin holds either only through an
 * explicit grant or invitation. `connect` was the first grant-by-exception module: its single string
 * is new, nobody holds it by history, and switching it off org-wide is how an organization says it
 * does not allow AI connectors at all.
 *
 * `donors` and `enrollment` remain in the backend's `UNPARTITIONED_PERMISSION_SLICES` as module-shaped
 * strings that have not been partitioned into catalogs yet. Labelling them now would ship words for
 * modules the console cannot render, and guessing their slugs is how a label ends up pointing at the
 * wrong thing — the portal's own nav says "Contacts", but the backend's pinned slug is `donors`.
 * Add each one in the change that partitions it.
 */
const MODULE_LABELS: Record<string, string> = {
  // The Admin Console's own slug on the wire (`module: "org"` on a preference definition, shell
  // contract v3). Not a product; the Admin Console's word is the right one.
  [ADMIN_CONSOLE_SLUG]: ADMIN_CONSOLE_LABEL,
  // CPA-BE28: the class program's own module, on for every claimed charity.
  donations: 'Donations',
  fundraisers: 'Fundraisers',
  board: 'Board governance',
  // The module's own word, as its tile says it (CCM-R1-REMIND-WEB). A page's word is not the
  // module's: its pages are Calendar · Filings · Documents (set C Q1).
  compliance: 'Compliance',
  connect: 'Connected AI',
};

/**
 * Display copy for the module permission keys this host presents.
 *
 * Grouped by module and, within a module, Member-level strings before Owner-level ones, matching the
 * catalog's own declaration order so a diff against the backend reads straight down.
 *
 * Each sentence says what the person may DO, in the portal's words rather than the key's. The
 * distinction the board catalog draws is the one worth keeping visible: the Member strings are
 * day-to-day recording work that binds the organization to nothing, while the Owner strings are the
 * acts that produce or alter the charity's own record.
 */
const PERMISSION_LABELS: Record<string, string> = {
  'charity_fundraiser:write': 'Create and manage fundraisers',
  'charity_p2p:manage': 'Manage campaign teams and supporters',
  'fundraisers:invite_admins': 'Invite people to Fundraisers',
  'fundraisers:approve_access_requests': 'Approve requests to open Fundraisers',
  // The donations module (CPA-BE28): the Member catalog's read; the Owner catalog's payment write
  // (D-CP-42), its approval string (R9) and its invite door (OM-10 clause 7). The first two are
  // Jonathan's words (D-CPO-77, 2026-10-24), shown wherever a level lists what it gives as well as in
  // Customize: a Member reads the payment setup with the bank account's numbers masked (every
  // payout-profile read returns the last four digits only), and the payment write sets up the bank
  // account or elects a check.
  'donations:read': 'View donations and payments, with bank account numbers hidden',
  'charity_payment:write': 'Set up how grants are paid: bank account or check',
  'donations:approve_access_requests': 'Approve requests to open Donations',
  'donations:invite_admins': 'Invite people to Donations',
  // ── board · Member ──────────────────────────────────────────────────────────
  'board:read': 'Read board meetings and records',
  'board:invite_admins': 'Invite people to Board governance',
  'board:approve_access_requests': 'Approve requests to open Board governance',
  'board_record:write': 'Write and edit draft board records',
  'board_member:grant_access': 'Give a director access to the board portal',
  'board_meeting:write': 'Set up meetings, agendas and packets',
  // ── board · Owner ───────────────────────────────────────────────────────────
  // The one door from a draft to the organization's actual record.
  'board_record:approve': 'Approve a draft into the board’s record',
  'board_record:import': 'Bring in historical board records',
  'board_record:delete': 'Delete a board record',
  'board_retention:manage': 'Decide how long board records are kept',
  'board_legal_hold:manage': 'Place and lift legal holds',
  // Roster COMPOSITION — who sits on the board — as distinct from handing a director a way in.
  'board_member:manage': 'Change who sits on the board',
  'board_governance_parameter:manage':
    'Set the governance figures notice periods are worked out from',
  'board_consent:manage': 'Record written consents',
  'board_conflict_disclosure:manage': 'Record a director’s conflict disclosure on their behalf',
  // ── compliance · Member ─────────────────────────────────────────────────────
  // `compliance:read` is minted with the D-CPO-24 flip (CCM-MOD1B): the module's thirteen reads
  // move off the org-baseline `charity:read` onto it, so a Member holds the register only by grant.
  'compliance:read': 'View the calendar, filings and documents',
  'charity_compliance:write': 'Keep the filing calendar and record what a state issued',
  // ── compliance · Owner ──────────────────────────────────────────────────────
  // CCM-R1-SIGN-PERM-WEB: the Owner catalog's three strings, each worded as the act it allows, the
  // last two on Fundraisers' pattern. `send_for_signature` (D-SIGN-2) also governs the sign-in check
  // before signing; it and `invite_admins` (D-SIGN-7) reach nobody until signing is released.
  // S7–S9, approved on the cut's renders (D-CCM-29, 2026-10-03).
  'compliance:approve_access_requests': 'Approve requests to open Compliance',
  'compliance:invite_admins': 'Invite people to Compliance',
  'compliance:send_for_signature': 'Send filing documents for signature',
  // ── connect · Owner ─────────────────────────────────────────────────────────
  // The act of CONNECTING one. What a connector may then do is the delegating human's own live
  // authority, re-resolved on every call — never this string, and never a stored copy of it.
  'charity:connect_ai': 'Connect an AI to this organization',
  // Its approval string (OM-14 PR-BE1, R9), in its siblings' ruled pattern ("Approve requests to
  // open {app}"): without it the line under Connected AI's Owner level printed the raw key.
  'connect:approve_access_requests': 'Approve requests to open Connected AI',
  // ── projects · Member ───────────────────────────────────────────────────────
  // PMM-BE1's neutral catalog (`org_module.py` `PROJECTS_MEMBER_PERMISSIONS`), byte-identical in the
  // company class, so the company host words it the same. The module has no web screens yet, but
  // its Apps card, access editor and invite composer list these keys wherever it is offered.
  'projects:read': 'View plans',
  'projects:write': 'Create and edit plans',
  // ── projects · Owner ────────────────────────────────────────────────────────
  'projects:invite_admins': 'Invite people to Projects',
  'projects:approve_access_requests': 'Approve requests to open Projects',
};

export function charityOrgConsoleVocab(orgName: string): OrgConsoleVocab {
  const named = orgName.trim();
  return {
    orgNoun: 'organization',
    // The switch page is the ONE surface that must name the organization (the package's own rule):
    // turning an app off closes it for everyone there.
    ...(named.length > 0 ? { orgName: named } : {}),
    membersTitle: 'Members',
    membersDescription:
      named.length > 0
        ? `Who can act for ${named} on Givmo.`
        : 'Who can act for your organization on Givmo.',
    moduleLabel: (slug) => MODULE_LABELS[slug] ?? titleizeSlug(slug),
    permissionLabel: (key) => PERMISSION_LABELS[key] ?? key,
    deactivation: CHARITY_DEACTIVATION_COPY,
  };
}
